import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { DialogAnimate } from 'src/components/animate';
import { GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { SkeletonForm } from 'src/components/skeleton';
import { GAME_ENTRY_FEE_REWARD } from 'src/constants/queryKeys';
import { networkCreateGameEntryFeeRewards, networkUpdateGameEntryFeeRewards } from 'src/network/game-entry-fee-rewards';
import { ContentSelectionTableModal } from 'src/sections/@dashboard/project/feedItems';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import useModalsStore from 'src/store/modals';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';
import GeneralDriverTextFiled from 'src/components/general/GeneralDriverTextFiled';

const GameEntryFeeRewardsForm = () => {
  const { modal, closeModal } = useFormsModalStore();
  const { isOpenGamesModal, setIsOpenGamesModal } = useModalsStore();

  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { appInfo } = useAppInfoStore();

  const [isLoading, setIsLoading] = useState(false);

  const [gameDetails, setGameDetails] = useState();

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const gameEntryFeeRewardDetails = modal?.data;
  const isEditMode = !!gameEntryFeeRewardDetails;

  // ---------------------------------------------------------------

  const defaultValues = getDefaultValues(gameEntryFeeRewardDetails);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GameEntryFeeRewardsApplicationID: +projectId,
      GameEntryFeeRewardsGameID: gameDetails?.GameID,
      GameEntryFeeRewardsTransactionAmountTypeID: +selectedAmountType,
      GameEntryFeeRewardsScore: +values?.GameEntryFeeRewardsScore,
      GameEntryFeeRewardsAmount: +values?.GameEntryFeeRewardsAmount,
    };
    
    try {
      if (isEditMode) {
        const res = await networkUpdateGameEntryFeeRewards(gameEntryFeeRewardDetails?.GameEntryFeeRewardsID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پاداش بازی با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GAME_ENTRY_FEE_REWARD] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGameEntryFeeRewards(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پاداش بازی با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [GAME_ENTRY_FEE_REWARD] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setValue('GameEntryFeeRewardsGameID', gameEntryFeeRewardDetails?.GameName);
    setGameDetails(gameEntryFeeRewardDetails);
  }, []);

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(gameEntryFeeRewardDetails?.GameEntryFeeRewardsTransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  const selectGameHandler = (item) => {
    setGameDetails(item);
    setValue('GameEntryFeeRewardsGameID', item?.GameName);
    setIsOpenGamesModal(false);
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                    width: '100%',
                  }}
                >
                  <SkeletonForm number={3} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField
                      name="GameEntryFeeRewardsGameID"
                      type="text"
                      label="بازی"
                      InputProps={{
                        endAdornment: (
                          <Button variant="contained" color="inherit" onClick={() => setIsOpenGamesModal(true)}>
                            انتخاب
                          </Button>
                        ),
                      }}
                    />
                    <GeneralSelectMenu
                      label="نوع واحد پاداش"
                      name="GameEntryFeeRewardsTransactionAmountTypeID"
                      options={amountTypes}
                      value={selectedAmountType}
                      onChangeValue={setSelectedAmountType}
                    />
                    <GeneralDriverTextFiled
                      name="GameEntryFeeRewardsScore"
                      type="text"
                      label="حداقل امتیاز"
                      helperDesc={'حداقل امتیاز برای دریافت پاداش'}
                      helperTitle={' '}
                    />
                    <RHFTextField name="GameEntryFeeRewardsAmount" type="text" label="تعداد پاداش" />
                  </Box>

                  <div className="flex items-center gap-4 mt-6">
                    <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                      ذخیره
                    </LoadingButton>
                    <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                      {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                    </LoadingButton>
                    <Button onClick={closeModal} variant="contained" color="error">
                      انصراف
                    </Button>
                  </div>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </FormProvider>

      <DialogAnimate open={isOpenGamesModal} onClose={() => setIsOpenGamesModal(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>انتخاب بازی</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <ContentSelectionTableModal selectContentHandler={selectGameHandler} />
        </div>
      </DialogAnimate>
    </>
  );
};

export default GameEntryFeeRewardsForm;
